/**
 * Trattative — CRM Kanban per trattative potenziali in arrivo dalla landing personale.
 *
 * 4 colonne: Nuove · Contattate · In trattativa · Chiuse
 * Card mostra: nome, città/area, stima, score, motivo, timing.
 * Click → dettaglio in side-panel con timeline + bottoni Chiama/WhatsApp/Email.
 *
 * Source: GET /api/mandates/list + GET /api/mandates/stats + PATCH /api/mandates/:id/status
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  Phone, WhatsappLogo, EnvelopeSimple, Copy, CheckCircle, X, Clock,
  TrendUp, Eye, MapPin,
} from "@phosphor-icons/react";
import VolantinoStimaModal from "@/components/VolantinoStimaModal";

const API = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { id: "new", label: "Nuove", color: "#7C8B6E", emoji: "🔥" },
  { id: "contacted", label: "Contattate", color: "#C8A66B", emoji: "📞" },
  { id: "negotiating", label: "In trattativa", color: "#5B7DB1", emoji: "🤝" },
  { id: "won", label: "Chiuse", color: "#3D8B5C", emoji: "✓" },
];

const formatCurrency = (n) => n ? `€${Math.round(n/1000)}k` : "—";

export default function Trattative() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [landing, setLanding] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [volantinoOpen, setVolantinoOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [l, s, ml] = await Promise.all([
        api.get(`/mandates/list`),
        api.get(`/mandates/stats`),
        api.get(`/mandates/my-landing`),
      ]);
      setLeads(l.data.items || []);
      setStats(s.data);
      setLanding(ml.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateStatus = async (lead_id, status) => {
    try {
      await api.patch(`/mandates/${lead_id}/status`, { status });
      await fetchAll();
      if (selectedLead?.id === lead_id) {
        setSelectedLead({ ...selectedLead, status });
      }
    } catch (e) {
      alert("Errore aggiornamento stato");
    }
  };

  // iter 275 round 39: usa sempre window.location.origin (preview vs prod).
  const publicUrl = landing ? `${window.location.origin}${landing.public_url}` : "";

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = async () => {
    // iter 275 round 39: blob+<a download> non funziona su iOS Safari.
    // Su iOS apriamo in nuova tab → l'utente fa long-press → "Salva su Foto".
    try {
      const r = await api.get("/mandates/qr.png", { responseType: "blob" });
      const blobUrl = URL.createObjectURL(r.data);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      if (isIOS) {
        window.open(blobUrl, "_blank", "noopener,noreferrer");
      } else {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = "qr-pronto.png";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 8000);
    } catch (e) {
      // silenzioso: il bottone è opzionale
    }
  };

  if (loading) {
    return (
      <AppShell title="Trattative" breadcrumb="Trattative">
        <div className="p-8 text-center text-[#7C8B6E] italic">Caricamento trattative…</div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Trattative" breadcrumb="Trattative">
    <div className="p-4 sm:p-6 lg:p-8" data-testid="trattative-page">

      {/* Header + stats */}
      <div className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div>
            <h1 className="font-editorial text-3xl sm:text-4xl tracking-tight text-[#1C1C1A]">
              Trattative <em className="italic text-[#7C8B6E]">potenziali</em>
            </h1>
            <p className="text-sm text-[#6E6A60] mt-1">
              Proprietari che hanno fatto la stima sulla tua pagina personale.
            </p>
          </div>

          {landing?.slug && (
            <div className="bg-[#1C1C1A] text-white rounded-xl p-4 min-w-[280px]" data-testid="landing-quick">
              <div className="text-[10.5px] uppercase tracking-widest text-[#A8B89A] font-bold mb-2">
                · La tua landing personale ·
              </div>
              <div className="font-editorial text-base mb-3 break-all" data-testid="landing-url">
                {publicUrl}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={copyLink} className="text-xs px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 transition flex items-center gap-1.5 font-semibold" data-testid="btn-copy-link">
                  {copied ? <><CheckCircle size={14} weight="fill" /> Copiato</> : <><Copy size={14} weight="bold" /> Copia link</>}
                </button>
                <button onClick={downloadQR} className="text-xs px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 transition font-semibold" data-testid="btn-qr">QR.png</button>
                <button onClick={() => setVolantinoOpen(true)} className="text-xs px-3 py-1.5 rounded-md bg-[#7C8B6E] hover:bg-[#A8B89A] hover:text-[#1C1C1A] transition font-semibold flex items-center gap-1.5" data-testid="btn-volantino">
                  Scarica volantino stima
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-strip">
            <div className="bg-[#F1F4ED] border border-[#D8E0CC] rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#7C8B6E] font-bold mb-1 flex items-center gap-1"><Eye size={11} weight="bold"/> Visite 7g</div>
              <div className="font-editorial text-3xl font-medium">{stats.visits_last_7d}</div>
            </div>
            <div className="bg-[#FEF3D7] border border-[#E8D199] rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#8A6F3D] font-bold mb-1 flex items-center gap-1"><TrendUp size={11} weight="bold"/> Conv. 7g</div>
              <div className="font-editorial text-3xl font-medium">{stats.conversion_rate}%</div>
            </div>
            <div className="bg-white border border-[#EAE7DF] rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#6E6A60] font-bold mb-1">Trattative 7g</div>
              <div className="font-editorial text-3xl font-medium">{stats.leads_last_7d}</div>
            </div>
            <div className="bg-white border border-[#EAE7DF] rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#6E6A60] font-bold mb-1">In pipeline</div>
              <div className="font-editorial text-3xl font-medium">{stats.by_status.new + stats.by_status.contacted + stats.by_status.negotiating}</div>
            </div>
          </div>
        )}
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="kanban">
        {COLUMNS.map(col => {
          const colLeads = leads.filter(l => l.status === col.id);
          return (
            <div key={col.id} className="bg-[#FAFAF7] border border-[#EAE7DF] rounded-xl p-3 min-h-[400px]" data-testid={`column-${col.id}`}>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#EAE7DF]">
                <div className="flex items-center gap-2">
                  <span>{col.emoji}</span>
                  <h3 className="font-bold text-sm uppercase tracking-wide" style={{ color: col.color }}>{col.label}</h3>
                </div>
                <span className="text-xs font-bold text-[#6E6A60]">{colLeads.length}</span>
              </div>

              <div className="space-y-2">
                {colLeads.length === 0 && (
                  <div className="text-xs text-[#9A968B] italic text-center py-8">Nessuna</div>
                )}
                {colLeads.map(lead => (
                  <button
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className="w-full text-left bg-white border border-[#EAE7DF] rounded-lg p-3 hover:shadow-md transition"
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-semibold text-sm text-[#1C1C1A] truncate">{lead.name}</div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{
                        background: lead.score >= 80 ? "#FEF3D7" : "#F1F4ED",
                        color: lead.score >= 80 ? "#8A6F3D" : "#7C8B6E",
                      }}>{lead.score}</span>
                    </div>
                    <div className="text-xs text-[#6E6A60] flex items-center gap-1 mb-1">
                      <MapPin size={10} weight="bold" />
                      {lead.estimate?.city_key || "—"}
                    </div>
                    {lead.estimate && (
                      <div className="text-xs font-semibold text-[#1C1C1A]">
                        {formatCurrency(lead.estimate.range_min)}–{formatCurrency(lead.estimate.range_max)}
                      </div>
                    )}
                    {lead.motivation && (
                      <div className="text-[10.5px] text-[#9A968B] mt-1 italic line-clamp-1">"{lead.motivation}"</div>
                    )}
                    {lead.timing && (
                      <div className="text-[10px] text-[#7C8B6E] mt-1 flex items-center gap-1 font-semibold">
                        <Clock size={10} weight="bold" />
                        {lead.timing.replace(/-/g, " ")}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {leads.length === 0 && (
        <div className="mt-8 text-center max-w-md mx-auto py-12" data-testid="empty-state">
          <div className="text-5xl mb-3">🌱</div>
          <h3 className="font-editorial text-2xl mb-2">Nessuna trattativa ancora.</h3>
          <p className="text-sm text-[#6E6A60] mb-6">
            Condividi il tuo link <strong>{publicUrl}</strong> ovunque puoi:
            bio Instagram, status WhatsApp, QR sui volantini.
            La prima trattativa arriva nelle prossime 48h.
          </p>
          <button onClick={() => setVolantinoOpen(true)} className="px-5 py-3 bg-[#7C8B6E] text-white rounded-lg font-semibold hover:bg-[#6A7A5E]" data-testid="btn-volantino-empty">
            Scarica il volantino "Quanto vale casa tua?"
          </button>
        </div>
      )}

      {/* Detail side-panel */}
      {selectedLead && (
        <LeadDetailPanel
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={(status) => updateStatus(selectedLead.id, status)}
        />
      )}

      <VolantinoStimaModal open={volantinoOpen} onClose={() => setVolantinoOpen(false)} />
    </div>
    </AppShell>
  );
}


function LeadDetailPanel({ lead, onClose, onUpdate }) {
  const phoneDigits = (lead.phone || "").replace(/[^\d+]/g, "");
  const waLink = `https://wa.me/${phoneDigits.replace(/^\+/, "")}?text=${encodeURIComponent(lead.wa_template || "")}`;
  const telLink = `tel:${phoneDigits}`;
  const mailLink = `mailto:${lead.email}?subject=${encodeURIComponent("La sua valutazione immobile")}`;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="lead-detail-panel">
      <div className="ml-auto w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#EAE7DF] p-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7C8B6E] font-bold">Trattativa potenziale</div>
            <div className="font-editorial text-xl">{lead.name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#FAFAF7] rounded-lg" data-testid="btn-close-detail"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Score + status */}
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-1 rounded font-bold" style={{
              background: lead.score >= 80 ? "#FEF3D7" : "#F1F4ED",
              color: lead.score >= 80 ? "#8A6F3D" : "#7C8B6E",
            }}>Score {lead.score}/100</span>
            <span className="text-xs px-2 py-1 rounded bg-[#1C1C1A] text-white font-bold uppercase tracking-wide">{lead.status}</span>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-3 gap-2">
            <a href={telLink} className="flex flex-col items-center gap-1 py-3 bg-[#7C8B6E] text-white rounded-lg font-semibold text-xs hover:bg-[#6A7A5E]" data-testid="action-call">
              <Phone size={20} weight="fill" /> Chiama
            </a>
            <a href={waLink} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 py-3 bg-[#25D366] text-white rounded-lg font-semibold text-xs hover:opacity-90" data-testid="action-whatsapp">
              <WhatsappLogo size={20} weight="fill" /> WhatsApp
            </a>
            <a href={mailLink} className="flex flex-col items-center gap-1 py-3 bg-[#1C1C1A] text-white rounded-lg font-semibold text-xs hover:bg-[#3A3A36]" data-testid="action-email">
              <EnvelopeSimple size={20} weight="fill" /> Email
            </a>
          </div>

          {/* WhatsApp template */}
          {lead.wa_template && (
            <div className="bg-[#DCF8C6]/40 border border-[#A8D8A8] rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#3D8B5C] font-bold mb-2">
                · Messaggio WhatsApp pronto ·
              </div>
              <p className="text-sm text-[#1C1C1A] leading-relaxed italic">"{lead.wa_template}"</p>
              <button
                onClick={() => navigator.clipboard.writeText(lead.wa_template)}
                className="mt-2 text-xs font-semibold text-[#3D8B5C] hover:underline"
                data-testid="copy-wa-template"
              >Copia messaggio</button>
            </div>
          )}

          {/* Stima */}
          {lead.estimate && (
            <div className="bg-[#1C1C1A] text-white rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#A8B89A] font-bold mb-1">Stima OMI</div>
              <div className="font-editorial text-2xl">
                {formatCurrency(lead.estimate.range_min)} <em className="italic text-[#A8B89A]">—</em> {formatCurrency(lead.estimate.range_max)}
              </div>
              <div className="text-[10px] text-white/60 mt-1">
                {lead.estimate.city_key} · €{lead.estimate.price_per_sqm}/mq · {lead.estimate.confidence}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="text-sm space-y-2">
            <div><strong>Email:</strong> {lead.email}</div>
            <div><strong>Telefono:</strong> {lead.phone}</div>
            {lead.motivation && <div><strong>Motivo:</strong> {lead.motivation}</div>}
            {lead.timing && <div><strong>Tempi:</strong> {lead.timing.replace(/-/g, " ")}</div>}
          </div>

          {/* Status update buttons */}
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#6E6A60] mb-2">Sposta in</div>
            <div className="grid grid-cols-2 gap-2">
              {COLUMNS.filter(c => c.id !== lead.status).map(c => (
                <button
                  key={c.id}
                  onClick={() => onUpdate(c.id)}
                  className="px-3 py-2 rounded-lg border-2 text-sm font-semibold hover:bg-[#FAFAF7]"
                  style={{ borderColor: c.color, color: c.color }}
                  data-testid={`move-${c.id}`}
                >{c.emoji} {c.label}</button>
              ))}
              <button
                onClick={() => onUpdate("lost")}
                className="px-3 py-2 rounded-lg border-2 text-sm font-semibold border-[#C44] text-[#C44] hover:bg-[#FFE8E8]"
                data-testid="move-lost"
              >❌ Perso</button>
            </div>
          </div>

          {/* Timeline */}
          {lead.timeline?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-[#6E6A60] mb-2">Storia</div>
              <div className="space-y-2">
                {lead.timeline.map((ev, i) => (
                  <div key={i} className="text-xs border-l-2 border-[#EAE7DF] pl-3">
                    <div className="font-semibold text-[#1C1C1A]">{ev.event}</div>
                    <div className="text-[10.5px] text-[#9A968B]">
                      {new Date(ev.at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                    {ev.note && <div className="text-[#6E6A60] italic mt-1">{ev.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
